import { createApi } from '@reduxjs/toolkit/query/react';

import { formatShows } from '@redux/helpers/shows';
import { likesFormatter } from '@redux/helpers/likes';
import { channelsSlice } from '@redux/state/channelsSlice';
import { likesSlice } from '@redux/state/likesSlice';
import { feedSlice } from '@redux/state/feedSlice';
import { participantsSlice } from '@redux/state/participantsSlice';
import { searchSlice } from '@redux/state/searchSlice';
import { showsSlice } from '@redux/state/showsSlice';
import { speakersSlice } from '@redux/state/speakersSlice';
import { topicsSlice } from '@redux/state/topicsSlice';
import { usersSlice } from '@redux/state/usersSlice';
import { AppState } from '@redux/store';
import {
  participantsMapper,
  topUsersMapper,
} from '@redux/helpers/participants';
import {
  Speaker,
  ParticipantServerData,
  ParticipantStatus,
  ParticipantRole,
  // Participant,
} from '@typings/participant';
import {
  HostStatistics,
  ParticipantStatistics,
  RecordedShowServerData,
  Show,
  // ShowCreateData,
  ShowServerData,
  ShowStatus,
  Topic,
} from '@typings/show';
import { User } from '@typings/user';
import { SHOWS_PER_PAGE } from '@utils/constants';

import { dynamicBaseQuery } from '../baseQuery';

export const showApi = createApi({
  reducerPath: 'showApi',
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    // createShow: builder.mutation<ShowServerData, ShowCreateData>({
    //   query: ({ image, ...rest }) => {
    //     const bodyFormData = new FormData();
    //     if (image && typeof image === 'object') {
    //       bodyFormData.append('image', {
    //         uri: image.path,
    //         type: image.mime,
    //         name: `${new Date().getTime()}-${image.filename}`,
    //       });
    //     }
    //     Object.entries(rest).forEach(([key, value]) => {
    //       if (value !== null) {
    //         bodyFormData.append(key, value);
    //       }
    //     });
    //     return {
    //       url: '/show',
    //       method: 'POST',
    //       body: bodyFormData,
    //       headers: {
    //         Accept: 'application/json',
    //         'Content-Type': 'multipart/form-data',
    //       },
    //       formData: true,
    //     };
    //   },
    //   onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
    //     try {
    //       const { data } = await queryFulfilled;
    //       if (data?.id) {
    //         const { host, coHost, participants, channel, ...restData } = data;
    //         const participantsData: Record<string, Participant> =
    //           participants.reduce(
    //             (acc, participant) => {
    //               if (participant.role === ParticipantRole.Host) {
    //                 acc[participant.id] = {
    //                   ...participant,
    //                   profileId: host.id,
    //                   showId: data.id,
    //                 };
    //               }

    //               if (participant.role === ParticipantRole.CoHost) {
    //                 acc[participant.id] = {
    //                   ...participant,
    //                   profileId: coHost?.id,
    //                   showId: data.id,
    //                 };
    //               }
    //               return acc;
    //             },
    //             {} as Record<string, Participant>,
    //           );

    //         dispatch(
    //           showsSlice.actions.setShowDataById({
    //             ...restData,
    //             listenersCount: 0,
    //             hostId: host.id,
    //             coHostId: coHost?.id,
    //             rightTeamSpeakerId: null,
    //             leftTeamSpeakerId: null,
    //             channelId: channel?.id,
    //           }),
    //         );
    //         if (
    //           data?.scheduledShow &&
    //           data?.scheduledShow.id &&
    //           data?.scheduledShow.startAt
    //         ) {
    //           dispatch(
    //             feedSlice.actions.addCreatedScheduledShowToFeedIds(data.id),
    //           );
    //         } else {
    //           dispatch(showsSlice.actions.setCurrentShowId(data.id));
    //           dispatch(feedSlice.actions.addCreatedShowToSuggestedIds(data.id));
    //         }
    //         dispatch(
    //           participantsSlice.actions.setParticipantsData(
    //             Object.values(participantsData),
    //           ),
    //         );

    //         if (channel) {
    //           dispatch(channelsSlice.actions.setChannelDataById(channel));
    //         }
    //       }
    //     } catch (error) {
    //       console.log('createShow error', error);
    //     }
    //   },
    // }),
    joinToShow: builder.mutation<
      ParticipantServerData & { show: { id: string }; token?: string },
      { showId: string; isAnonymous?: boolean; token?: string }
    >({
      query: ({ showId, isAnonymous, token }) => {
        if (isAnonymous && token) {
          return {
            url: `/shows/${showId}/join-anonymous`,
            method: 'POST',
            body: { token },
          };
        }
        return {
          url: `/shows/${showId}/join`,
          method: 'POST',
        };
      },
      onQueryStarted: async ({ isAnonymous }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.id) {
            if (isAnonymous) {
              const { show, ...participantData } = data;
              dispatch(
                participantsSlice.actions.setParticipantDataById({
                  ...participantData,
                  profileId: data?.token,
                  showId: show.id,
                }),
              );
              dispatch(showsSlice.actions.addParticipantToShow(data));
            } else {
              const { profile, show, ...participantData } = data;
              dispatch(
                participantsSlice.actions.setParticipantDataById({
                  ...participantData,
                  profileId: profile.id,
                  showId: show.id,
                }),
              );
              dispatch(usersSlice.actions.setUserDataById(data.profile));
              dispatch(showsSlice.actions.addParticipantToShow(data));
            }
          }
        } catch (error) {
          console.log('joinToShow error', error);
        }
      },
    }),
    finishShow: builder.mutation<
      {
        show: ShowServerData;
        topics: Topic[];
        statistics: HostStatistics & ParticipantStatistics;
      },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/shows/${id}/finish`,
        method: 'PUT',
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const {
            data: { show, statistics, topics },
          } = await queryFulfilled;
          if (show?.id) {
            let topUsers: { userId: string; likesCount: number }[] = [];
            if (statistics?.topUsers?.length) {
              const { usersData, topUsersList } = topUsersMapper(
                statistics.topUsers,
              );
              topUsers = topUsersList;
              dispatch(
                usersSlice.actions.setUsersData(Object.values(usersData)),
              );
            }
            dispatch(topicsSlice.actions.setTopicsData(topics));
            dispatch(
              showsSlice.actions.setShowDataById({
                ...show,
                statistics: {
                  ...statistics,
                  topUsers,
                  debates: !topics?.length
                    ? []
                    : topics.map((topic) => topic.id),
                },
              }),
            );
            dispatch(
              feedSlice.actions.removeLiveShowFromSuggestedIds({
                id: show.id,
              }),
            );
          }
        } catch (error) {
          console.log('finishShow error', error);
        }
      },
    }),
    getAllShows: builder.query<
      ShowServerData[],
      {
        isRefresh: boolean;
        perPage?: number;
        lastId?: string;
        filters?: string[];
        search?: string;
      }
    >({
      query: ({ perPage = SHOWS_PER_PAGE, filters, search, lastId }) => {
        return {
          url: '/shows',
          params: { perPage, filters, search: search?.trim(), lastId },
          method: 'GET',
        };
      },
      keepUnusedDataFor: 3,
      onQueryStarted: async ({ isRefresh }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          if (!data.length) {
            dispatch(searchSlice.actions.setShowsIds({ ids: [], isRefresh }));
            return;
          }

          const {
            allUsersData,
            allParticipantsData,
            allSpeakersData,
            allChannelsData,
            ids,
            formattedShows,
          } = formatShows(data);
          dispatch(
            usersSlice.actions.setUsersData(Object.values(allUsersData)),
          );
          dispatch(
            participantsSlice.actions.setParticipantsData(
              Object.values(allParticipantsData),
            ),
          );
          dispatch(
            speakersSlice.actions.setSpeakersData(
              Object.values(allSpeakersData),
            ),
          );
          if (Object.keys(allChannelsData).length) {
            dispatch(
              channelsSlice.actions.setChannelsData(
                Object.values(allChannelsData),
              ),
            );
          }
          dispatch(showsSlice.actions.setShowsData(formattedShows));
          dispatch(searchSlice.actions.setShowsIds({ ids, isRefresh }));
        } catch (error) {
          console.error('getAllShows error', error);
          dispatch(searchSlice.actions.setShowsIds({ ids: [], isRefresh }));
        }
      },
    }),
    getShowById: builder.query<
      ShowServerData & {
        topics?: Topic[];
        statistics?: HostStatistics & ParticipantStatistics;
      },
      { id: string; _ts?: number }
    >({
      query: ({ id }) => ({
        url: `/shows/${id}`,
      }),
      keepUnusedDataFor: 3,
      onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;
          const { auth, participants: participantsStateData } =
            getState() as AppState;

          if (data?.id && data?.status === ShowStatus.Live && auth.user?.id) {
            const {
              host,
              coHost,
              listenersCount,
              participants,
              speakers,
              channel,
              likes,
              statistics: _statistics,
              topics: _topics,
              ...show
            } = data;
            const {
              participantsData,
              usersData,
              rightTeamSpeakerId,
              leftTeamSpeakerId,
              speakersData,
            } = participantsMapper(participants, show.id, speakers);

            const formattedShow = {
              ...show,
              hostId: host.id,
              coHostId: coHost?.id,
              listenersCount: listenersCount || 0,
              rightTeamSpeakerId,
              leftTeamSpeakerId,
              channelId: channel?.id,
            };
            dispatch(showsSlice.actions.setShowDataById(formattedShow as Show));
            dispatch(
              participantsSlice.actions.setParticipantsData(
                Object.values(participantsData),
              ),
            );

            const currentUserParticipant = Object.values(
              participantsStateData.byId,
            ).find(
              (participant) =>
                participant.profileId === auth.user?.id &&
                participant.showId === show.id,
            );

            if (
              currentUserParticipant?.id &&
              currentUserParticipant.role === ParticipantRole.Speaker &&
              !participantsData[currentUserParticipant.id]
            ) {
              dispatch(
                participantsSlice.actions.setParticipantDataById({
                  id: currentUserParticipant.id,
                  role: ParticipantRole.Listener,
                  status: ParticipantStatus.Muted,
                }),
              );
            }

            dispatch(
              speakersSlice.actions.setSpeakersData(
                Object.values(speakersData),
              ),
            );
            dispatch(usersSlice.actions.setUsersData(Object.values(usersData)));

            if (channel) {
              dispatch(channelsSlice.actions.setChannelDataById(channel));
            }

            const { likesByUser, likesBySpeaker } = likesFormatter(likes);

            dispatch(
              likesSlice.actions.setLikesByUser({
                showId: data.id,
                likesByUser,
              }),
            );
            dispatch(
              likesSlice.actions.setLikesBySpeaker({
                showId: data.id,
                likesBySpeaker,
              }),
            );
          }

          if (
            data?.id &&
            data?.status === ShowStatus.Finished &&
            auth.user?.id
          ) {
            const { topics, statistics, ...showData } = data;
            let debates: string[] = [];
            const stats = statistics ? { ...statistics } : {};
            let topUsers: { userId: string; likesCount: number }[] = [];
            if (topics?.length) {
              debates = topics.map((topic) => topic.id);
              dispatch(topicsSlice.actions.setTopicsData(topics));
            }
            if (statistics && statistics.topUsers?.length) {
              const { usersData, topUsersList } = topUsersMapper(
                statistics.topUsers,
              );
              topUsers = topUsersList;
              dispatch(
                usersSlice.actions.setUsersData(Object.values(usersData)),
              );
            }
            dispatch(
              showsSlice.actions.setShowDataById({
                ...showData,
                statistics: {
                  ...stats,
                  topUsers,
                  debates,
                },
              }),
            );
          }
        } catch (error) {
          console.log('getShowById error', error);
        }
      },
    }),
    getRecordedShowById: builder.query<RecordedShowServerData, { id: string }>({
      query: ({ id }) => ({
        url: `/shows/${id}/recorded`,
      }),
      keepUnusedDataFor: 3,
      onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;
          const { auth } = getState() as AppState;

          if (data?.id && auth.user?.id) {
            const {
              host,
              coHost,
              channel,
              listenersCount,
              participants,
              speakers,
              topics,
              ...show
            } = data;
            const {
              participantsData,
              usersData,
              rightTeamSpeakerId,
              leftTeamSpeakerId,
            } = participantsMapper(participants, show.id, []);
            const { speakersData, usersSpeakersData } = Object.values(
              speakers || {},
            ).reduce(
              (
                acc: {
                  speakersData: { [key: string]: Speaker };
                  usersSpeakersData: { [key: string]: User };
                },
                speaker,
              ) => {
                acc.speakersData[speaker?.id] = {
                  id: speaker?.id,
                  profileId: speaker?.user?.id,
                  topicId: speaker?.topic?.id,
                  side: speaker?.side,
                  likesCount: 0,
                  participantId: speaker?.participant?.id,
                };
                acc.usersSpeakersData[speaker?.user?.id] = {
                  ...speaker?.user,
                } as User;
                return acc;
              },
              { speakersData: {}, usersSpeakersData: {} },
            );

            const formattedShow: Show = {
              ...show,
              hostId: host.id,
              coHostId: coHost?.id,
              listenersCount: listenersCount || 0,
              rightTeamSpeakerId,
              leftTeamSpeakerId,
              channelId: channel?.id,
            };

            dispatch(showsSlice.actions.setShowDataById(formattedShow));
            dispatch(topicsSlice.actions.setTopicsData(topics || []));
            dispatch(
              participantsSlice.actions.setParticipantsData(
                Object.values(participantsData),
              ),
            );
            dispatch(
              usersSlice.actions.setUsersData(
                Object.values({ ...usersData, ...usersSpeakersData }),
              ),
            );
            dispatch(
              speakersSlice.actions.setSpeakersData(
                Object.values(speakersData),
              ),
            );

            if (channel) {
              dispatch(channelsSlice.actions.setChannelDataById(channel));
            }
          }
        } catch (error) {
          console.log('getRecordedShowById error', error);
        }
      },
    }),
  }),
});

export const {
  // useCreateShowMutation,
  useJoinToShowMutation,
  useFinishShowMutation,
  useLazyGetAllShowsQuery,
  useLazyGetShowByIdQuery,
  useLazyGetRecordedShowByIdQuery,
} = showApi;
